import * as React from "react";
import { format, parse } from "date-fns";
import { es } from "react-day-picker/locale";
import { CalendarIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const ISO_DATE = "yyyy-MM-dd";
const DISPLAY_FORMAT = "dd/MM/yyyy";

function parseIsoDate(value: string): Date | undefined {
  if (!value?.trim()) return undefined;
  try {
    const d = parse(value.trim(), ISO_DATE, new Date());
    return isNaN(d.getTime()) ? undefined : d;
  } catch {
    return undefined;
  }
}

export interface DatePickerFieldProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  label: React.ReactNode;
  error?: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  /** Trigger button class name */
  triggerClassName?: string;
  /** When "inline", label is shown to the left of the input in one row; default "top" stacks label above */
  labelPosition?: "top" | "inline";
  /**
   * Earliest selectable month. Also bounds the year dropdown when
   * captionLayout='dropdown'. Defaults to Jan 1970 so the dropdown stays
   * useful for "activity start date" pickers that can reach into the 70s.
   */
  startMonth?: Date;
  /**
   * Latest selectable month. Defaults to ten years from today so users
   * planning a future activity start date can still pick it.
   */
  endMonth?: Date;
  /**
   * react-day-picker captionLayout. Default 'dropdown' so year + month
   * are selectable directly instead of clicking the chevron month-by-month.
   */
  captionLayout?: "label" | "dropdown" | "dropdown-months" | "dropdown-years";
  /**
   * Latest selectable DAY (inclusive). Unlike endMonth (which only bounds
   * the month/year dropdown), this disables individual days after the
   * given date. Opt-in — omit for pickers where a future day is valid
   * (activity start date, report ranges, recurring-schedule "once" date).
   */
  maxDate?: Date;
  /**
   * Earliest selectable DAY (inclusive). Symmetric to maxDate — disables
   * individual days before the given date. Opt-in for the same reason.
   */
  minDate?: Date;
}

/**
 * Computed once per module load — close enough for the 10-year-out
 * defaults; if the page lives in the browser across new year it just
 * means the dropdown's upper bound is "next year's December" rather
 * than "this year+10", which is fine.
 */
const DEFAULT_START_MONTH = new Date(1970, 0, 1);
const DEFAULT_END_MONTH = new Date(
  new Date().getFullYear() + 10,
  11,
  1,
);

export function DatePickerField({
  id,
  value,
  onChange,
  label,
  error,
  disabled,
  placeholder = "dd/mm/aaaa",
  className,
  triggerClassName,
  labelPosition = "top",
  startMonth = DEFAULT_START_MONTH,
  endMonth = DEFAULT_END_MONTH,
  captionLayout = "dropdown",
  maxDate,
  minDate,
}: DatePickerFieldProps): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const selectedDate = parseIsoDate(value);

  const handleSelect = React.useCallback(
    (date: Date | undefined) => {
      if (!date) return;
      onChange(format(date, ISO_DATE));
      setOpen(false);
    },
    [onChange]
  );

  const displayValue = selectedDate
    ? format(selectedDate, DISPLAY_FORMAT, { locale: es })
    : "";

  const disabledDays = React.useMemo(() => {
    const matchers = [];
    if (maxDate) matchers.push({ after: maxDate });
    if (minDate) matchers.push({ before: minDate });
    return matchers.length > 0 ? matchers : undefined;
  }, [maxDate, minDate]);

  const isInline = labelPosition === "inline";

  return (
    <div
      className={cn(
        isInline ? "flex flex-row items-center gap-2" : "space-y-2",
        className
      )}
    >
      {label && (
        <label
          htmlFor={id}
          className={cn(
            "text-sm font-medium leading-none",
            isInline && "shrink-0"
          )}
        >
          {label}
        </label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            disabled={disabled}
            className={cn(
              "w-full justify-start text-left font-normal",
              !displayValue && "text-muted-foreground",
              error && "border-destructive",
              triggerClassName
            )}
          >
            <CalendarIcon className="mr-2 size-4" />
            {displayValue || placeholder}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto rounded-[var(--fx-r-lg)] p-0 z-[100]"
          side="bottom"
          align="start"
          sideOffset={6}
          collisionPadding={12}
        >
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleSelect}
            locale={es}
            captionLayout={captionLayout}
            startMonth={startMonth}
            endMonth={endMonth}
            disabled={disabledDays}
            defaultMonth={selectedDate ?? new Date()}
          />
        </PopoverContent>
      </Popover>
      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
    </div>
  );
}
